import React, { useState, useEffect } from 'react';
import {
  Input,
  Dropdown,
  Option,
  RadioGroup,
  Radio,
  SpinButton,
  Label,
  Text,
} from '@fluentui/react-components';
import { ActiveFilters, Filter, FilterSelection } from '../../models/filters';

interface FiltersComponentProps {
  filter: Filter;
  activeFilters: ActiveFilters | null;
  disabled?: boolean;
  onFilterChange: (filter: Filter) => void;
}

export const FiltersComponent: React.FC<FiltersComponentProps> = ({
  filter,
  activeFilters,
  disabled = false,
  onFilterChange,
}) => {
  const [value, setValue] = useState<any>('');

  // Set default value based on filter type
  useEffect(() => {
    switch (filter.type) {
      case 'tenderingPart':
      case 'bool':
        if (filter.isRequired) {
          setValue('0');
        } else {
          setValue('3');
        }
        break;
      default:
        setValue('');
        break;
    }

    // Set active filter value if exists
    if (activeFilters) {
      // Check both baseFilters and filters arrays
      const found = activeFilters.baseFilters?.find(af => af.filterID === filter.filterID) 
        || activeFilters.filters?.find(af => af.filterID === filter.filterID);
      if (found) {
        setValue(found.value);
      }
    }
  }, [filter, activeFilters]);

  const handleChange = (newValue: any) => {
    
    const updatedFilter = { ...filter };
    
    switch (filter.type) {
      case 'text':
        setValue(newValue);
        updatedFilter.value = newValue === '' ? undefined : newValue;
        break;
      case 'select':
        // Store only the text in state for display, but process the full object
        setValue(newValue?.text || '');
        if (newValue && filter.separateQuestions) {
          updatedFilter.questionID = newValue.questionID;
          updatedFilter.value = newValue.text;
          if (filter.questionType === 'bool') {
            updatedFilter.value = '1';
          }
        } else {
          updatedFilter.value = newValue === null ? undefined : newValue?.text;
        }
        break;
      case 'bool':
        setValue(newValue);
        if (newValue === '1' || newValue === '0') {
          updatedFilter.value = newValue;
        } else {
          updatedFilter.value = undefined;
        }
        break;
      case 'int':
      case 'int-range':
        setValue(newValue);
        updatedFilter.value = newValue === '' ? undefined : parseInt(newValue);
        break;
      default:
        setValue(newValue);
        updatedFilter.value = newValue;
        break;
    }

    onFilterChange(updatedFilter);
  };

  const renderFilterInput = () => {
    switch (filter.type) {
      case 'select':
        return (
          <div className="flex flex-col space-y-2">
            <Label required={filter.isRequired}>
              {filter.text}
            </Label>
            <Dropdown
              value={value}
              disabled={disabled}
              placeholder="Välj ett alternativ"
              onOptionSelect={(_, data) => {
                // Find the full selection object based on the selected text
                const selectedItem = filter.selections?.find((s: any) => s.text === data.optionText);
                handleChange(selectedItem);
              }}
              clearable
            >
              {filter.selections?.map((selection: any, index:number) => (
                <Option key={index} value={selection.text} text={selection.text}>
                  {selection.text}
                </Option>
              ))}
            </Dropdown>
          </div>
        );

      case 'int-range':
        return (
          <div className="flex flex-col space-y-2">
            <Label required={filter.isRequired}>
              {filter.text}
            </Label>
            <SpinButton
              value={value}
              disabled={disabled}
              min={filter.min}
              max={filter.max}
              onChange={(_, data) => {data.value ? handleChange(data.value) : handleChange(data.displayValue)}}
            />
          </div>
        );

      case 'int':
        return (
          <div className="flex flex-col space-y-2">
            <Label required={filter.isRequired}>
              {filter.text}
            </Label>
            <SpinButton
              value={value}
              disabled={disabled}
              onChange={(_, data) => {data.value ? handleChange(data.value) : handleChange(data.displayValue)}}
            />
          </div>
        );

      case 'text':
        return (
          <div className="flex flex-col gap-1">
            <Label weight='semibold' required={filter.isRequired}>
              {filter.text}
            </Label>
            <Input
              type="text"
              value={value}
              disabled={disabled}
              onChange={(_, data) => handleChange(data.value)}
            />
          </div>
        );

      case 'bool':
        return (
          <div>
            <Label required={filter.isRequired}>
              {filter.text}
            </Label>
            <RadioGroup
              value={value}
              disabled={disabled}
              onChange={(_, data) => handleChange(data.value)}
              layout="horizontal"
            >
              {!filter.isRequired && (
                <Radio value="3" label="Inget Val" />
              )}
              <Radio value="1" label="Ja" />
              <Radio value="0" label="Nej" />
            </RadioGroup>
          </div>
        );

      default:
        return (
          <div className="space-y-2">
            <Label>{filter.text}</Label>
            <Text>Unsupported filter type: {filter.type}</Text>
          </div>
        );
    }
  };

  return (
    <div className="w-full">
      {renderFilterInput()}
    </div>
  );
};

export default FiltersComponent;
